// Substack API Integration
// Note: Substack doesn't have a public API, but they do have some undocumented endpoints
// For production use, consider using their RSS feed or email-based publishing

export interface SubstackPost {
  id?: string;
  title: string;
  subtitle?: string;
  content: string;
  publishedAt?: string;
  isDraft: boolean;
  slug?: string;
  coverImage?: string;
}

export interface SubstackConfig {
  publicationUrl: string; // e.g., "yourname.substack.com"
  email?: string;
  password?: string;
  sessionCookie?: string;
  csrfToken?: string;
}

export interface SyncStatus {
  postId: string;
  substackUrl?: string;
  syncedAt: string;
  status: 'pending' | 'synced' | 'failed';
  error?: string;
}

class SubstackAPI {
  private config: SubstackConfig;
  private baseUrl: string;

  constructor(config: SubstackConfig) {
    this.config = config;
    this.baseUrl = `https://${config.publicationUrl}`;
  }

  // Method 1: Email-based publishing (most reliable)
  async publishViaEmail(post: SubstackPost): Promise<{ success: boolean; error?: string }> {
    try {
      // This method involves sending an email to publish@substack.com
      // with specific formatting that Substack recognizes
      const emailBody = this.formatPostForEmail(post);
      
      // You would integrate with an email service here
      // For now, we'll return instructions
      return {
        success: true,
        error: "Email method: Send the formatted content to publish@substack.com"
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Method 2: RSS-based sync (read-only, good for verification)
  async getRSSFeed(): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/feed`);
      if (!response.ok) {
        throw new Error('Failed to fetch RSS feed');
      }
      
      const rssText = await response.text();
      // Parse RSS here - you'd need an RSS parser library
      // For now, returning empty array
      return [];
    } catch (error) {
      console.error('Error fetching RSS:', error);
      return [];
    }
  }

  // Method 3: Undocumented API (use with caution)
  async publishPost(post: SubstackPost): Promise<{ success: boolean; postUrl?: string; error?: string }> {
    try {
      // This is a placeholder for Substack's undocumented API
      // In reality, you'd need to:
      // 1. Authenticate with Substack
      // 2. Get CSRF tokens
      // 3. Make authenticated requests
      
      // For demonstration purposes, we'll simulate the API call
      const payload = {
        title: post.title,
        subtitle: post.subtitle || '',
        body: post.content,
        is_published: !post.isDraft,
        cover_image: post.coverImage,
        // Add other required fields
      };

      console.log('Would publish to Substack:', payload);
      
      // Simulate success response
      return {
        success: true,
        postUrl: `${this.baseUrl}/p/${this.slugify(post.title)}`,
        error: 'Simulated: Post would be published to Substack'
      };
      
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to publish to Substack'
      };
    }
  }

  // Helper method to format post for email publishing
  private formatPostForEmail(post: SubstackPost): string {
    const subject = `New Post: ${post.title}`;
    const body = `
Subject: ${post.title}
${post.subtitle ? `Subtitle: ${post.subtitle}` : ''}

${post.content}

${post.isDraft ? '(DRAFT - will not be published immediately)' : ''}
---
Published via automated sync from blog
    `.trim();

    return body;
  }

  // Helper method to create URL-friendly slugs
  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  // Method to validate configuration
  validateConfig(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!this.config.publicationUrl) {
      errors.push('Publication URL is required');
    }
    
    if (!this.config.publicationUrl.includes('substack.com')) {
      errors.push('Publication URL must be a valid Substack domain');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export the class and create a default instance
export default SubstackAPI;

// Utility functions for Substack sync
export const substackUtils = {
  // Convert blog post to Substack format
  convertBlogPostToSubstack: (blogPost: any): SubstackPost => {
    return {
      title: blogPost.title,
      subtitle: blogPost.excerpt || '',
      content: blogPost.content, // This should be HTML
      publishedAt: blogPost.date,
      isDraft: blogPost.isDraft || false,
      coverImage: blogPost.imageUrl,
      slug: blogPost.id
    };
  },

  // Convert markdown content to Substack-friendly HTML
  prepareContentForSubstack: (content: string): string => {
    // Basic cleanup for Substack
    // Remove any script tags or potentially problematic HTML
    return content
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/<form[^>]*>.*?<\/form>/gi, '');
  },

  // Extract plain text preview
  getTextPreview: (htmlContent: string, maxLength: number = 150): string => {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength 
      ? text.substring(0, maxLength) + '...' 
      : text;
  }
};

// Configuration helper
export const createSubstackConfig = (
  publicationUrl: string,
  options: Partial<SubstackConfig> = {}
): SubstackConfig => {
  return {
    publicationUrl: publicationUrl.replace(/^https?:\/\//, ''),
    ...options
  };
};